/**
 * AudioRecorder - Voice message recording using MediaRecorder API
 * Inspired by Rocket.Chat's AudioRecorder implementation
 */

class AudioRecorder {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
    this.startTime = null;
    this.isRecording = false;
    this.onDataAvailable = null;
    this.onStop = null;
    this.onError = null;
  }

  /**
   * Check if audio recording is supported in this browser
   */
  static isSupported() {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.MediaRecorder
    );
  }

  /**
   * Get supported MIME types for audio recording
   */
  static getSupportedMimeType() {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
      'audio/mp4',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return null;
  }

  /**
   * Get file extension based on MIME type
   */
  static getFileExtension(mimeType) {
    if (mimeType?.includes('webm')) return 'webm';
    if (mimeType?.includes('ogg')) return 'ogg';
    if (mimeType?.includes('mp4')) return 'm4a';
    return 'webm';
  }

  /**
   * Request microphone permission
   */
  static async requestPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately, we just wanted to check permission
      stream.getTracks().forEach(track => track.stop());
      return { granted: true };
    } catch (error) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        return { granted: false, denied: true, error: 'Microphone permission denied' };
      }
      return { granted: false, error: error.message };
    }
  }

  /**
   * Start recording audio
   */
  async start() {
    if (this.isRecording) {
      throw new Error('Already recording');
    }

    if (!AudioRecorder.isSupported()) {
      throw new Error('Audio recording not supported in this browser');
    }

    const mimeType = AudioRecorder.getSupportedMimeType();
    if (!mimeType) {
      throw new Error('No supported audio format found');
    }

    try {
      // Get microphone stream
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      // Create MediaRecorder
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        audioBitsPerSecond: 128000, // 128 kbps
      });

      this.audioChunks = [];
      this.startTime = Date.now();
      this.isRecording = true;

      // Handle data available
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
        if (this.onDataAvailable) {
          this.onDataAvailable(event.data);
        }
      };

      // Handle recording stop
      this.mediaRecorder.onstop = () => {
        const mimeType = this.mediaRecorder.mimeType;
        const blob = new Blob(this.audioChunks, { type: mimeType });
        const duration = this.startTime ? Date.now() - this.startTime : 0;

        if (this.onStop) {
          this.onStop({
            blob,
            mimeType,
            duration,
            extension: AudioRecorder.getFileExtension(mimeType),
          });
        }

        this.cleanup();
      };

      // Handle errors
      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        if (this.onError) {
          this.onError(event.error);
        }
        this.cleanup();
      };

      // Start recording with timeslice for periodic data
      this.mediaRecorder.start(1000); // Get data every second

      return {
        mimeType,
        startTime: this.startTime,
      };
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }

  /**
   * Stop recording and return the audio blob
   */
  stop() {
    return new Promise((resolve, reject) => {
      if (!this.isRecording || !this.mediaRecorder) {
        reject(new Error('Not recording'));
        return;
      }

      this.onStop = (result) => {
        resolve(result);
      };

      this.onError = (error) => {
        reject(error);
      };

      try {
        this.mediaRecorder.stop();
      } catch (error) {
        this.cleanup();
        reject(error);
      }
    });
  }

  /**
   * Cancel recording without saving
   */
  cancel() {
    if (this.mediaRecorder && this.isRecording) {
      this.onStop = null; // Don't call the callback
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        // Ignore errors when canceling
      }
    }
    this.cleanup();
  }

  /**
   * Get current recording duration in milliseconds
   */
  getDuration() {
    if (!this.startTime || !this.isRecording) {
      return 0;
    }
    return Date.now() - this.startTime;
  }

  /**
   * Check if currently recording
   */
  getIsRecording() {
    return this.isRecording;
  }

  /**
   * Clean up resources
   */
  cleanup() {
    this.isRecording = false;
    this.startTime = null;
    this.audioChunks = [];

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.mediaRecorder) {
      this.mediaRecorder.ondataavailable = null;
      this.mediaRecorder.onstop = null;
      this.mediaRecorder.onerror = null;
      this.mediaRecorder = null;
    }

    this.onDataAvailable = null;
    this.onStop = null;
    this.onError = null;
  }
}

export default AudioRecorder;
